import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  arrayUnion,
} from 'firebase/firestore';
import { getFirebaseDb } from './firebase';
import type {
  MemoryThread,
  ThreadEntry,
  ThreadStatus,
  Pattern,
  PatternOccurrence,
  Milestone,
  MemoryFact,
  MemoryEpisode,
  MemoryPlaybookEntry,
  MemoryCommitment,
  MemoryItemKind,
  SuppressionRule,
} from '../types';

// ─── Path Helpers ────────────────────────────────────────────────────────────

const paths = {
  threads: (userId: string) => collection(getFirebaseDb(), 'users', userId, 'threads'),
  thread: (userId: string, threadId: string) =>
    doc(getFirebaseDb(), 'users', userId, 'threads', threadId),
  patterns: (userId: string) => collection(getFirebaseDb(), 'users', userId, 'patterns'),
  pattern: (userId: string, patternId: string) =>
    doc(getFirebaseDb(), 'users', userId, 'patterns', patternId),
  milestones: (userId: string) => collection(getFirebaseDb(), 'users', userId, 'milestones'),
  milestone: (userId: string, milestoneId: string) =>
    doc(getFirebaseDb(), 'users', userId, 'milestones', milestoneId),
};

// ─── Memory Threads ──────────────────────────────────────────────────────────

export async function createThread(
  userId: string,
  data: Omit<MemoryThread, 'id' | 'userId' | 'createdAt' | 'lastUpdatedAt'>
): Promise<string> {
  const now = Timestamp.now();
  const ref = await addDoc(paths.threads(userId), {
    ...data,
    userId,
    createdAt: now,
    lastUpdatedAt: now,
  });
  return ref.id;
}

export async function getThread(userId: string, threadId: string): Promise<MemoryThread | null> {
  const snap = await getDoc(paths.thread(userId, threadId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as MemoryThread;
}

export async function getActiveThreads(userId: string): Promise<MemoryThread[]> {
  const q = query(
    paths.threads(userId),
    where('status', '==', 'active'),
    orderBy('lastUpdatedAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as MemoryThread));
}

export async function getAllThreads(userId: string): Promise<MemoryThread[]> {
  const snap = await getDocs(paths.threads(userId));
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as MemoryThread));
}

export async function updateThread(
  userId: string,
  threadId: string,
  updates: Partial<Omit<MemoryThread, 'id' | 'userId' | 'createdAt'>>
): Promise<void> {
  await updateDoc(paths.thread(userId, threadId), {
    ...updates,
    lastUpdatedAt: Timestamp.now(),
  });
}

export async function updateThreadStatus(
  userId: string,
  threadId: string,
  status: ThreadStatus
): Promise<void> {
  await updateDoc(paths.thread(userId, threadId), {
    status,
    lastUpdatedAt: Timestamp.now(),
  });
}

export async function addThreadEntry(
  userId: string,
  threadId: string,
  entry: ThreadEntry
): Promise<void> {
  await updateDoc(paths.thread(userId, threadId), {
    entries: arrayUnion(entry),
    lastUpdatedAt: Timestamp.now(),
  });
}

// ─── Patterns ────────────────────────────────────────────────────────────────

export async function createPattern(
  userId: string,
  data: Omit<Pattern, 'id' | 'userId' | 'createdAt' | 'updatedAt'>
): Promise<string> {
  const now = Timestamp.now();
  const ref = await addDoc(paths.patterns(userId), {
    ...data,
    userId,
    createdAt: now,
    updatedAt: now,
  });
  return ref.id;
}

export async function getPattern(userId: string, patternId: string): Promise<Pattern | null> {
  const snap = await getDoc(paths.pattern(userId, patternId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as Pattern;
}

export async function getAllPatterns(userId: string): Promise<Pattern[]> {
  const snap = await getDocs(paths.patterns(userId));
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as Pattern));
}

export async function getUnsurfacedPatterns(userId: string): Promise<Pattern[]> {
  const q = query(
    paths.patterns(userId),
    where('surfaced', '==', false),
    where('confidence', '>=', 0.7)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as Pattern));
}

export async function updatePattern(
  userId: string,
  patternId: string,
  updates: Partial<Omit<Pattern, 'id' | 'userId' | 'createdAt'>>
): Promise<void> {
  await updateDoc(paths.pattern(userId, patternId), {
    ...updates,
    updatedAt: Timestamp.now(),
  });
}

export async function addPatternOccurrence(
  userId: string,
  patternId: string,
  occurrence: PatternOccurrence
): Promise<void> {
  const patternSnap = await getDoc(paths.pattern(userId, patternId));
  if (!patternSnap.exists()) return;

  const data = patternSnap.data() as Pattern;
  const occurrences = [...data.occurrences, occurrence];
  // Simple confidence growth: each occurrence adds a bit until cap
  const newConfidence = Math.min(1, data.confidence + 0.1);

  await updateDoc(paths.pattern(userId, patternId), {
    occurrences,
    confidence: newConfidence,
    updatedAt: Timestamp.now(),
  });
}

export async function markPatternSurfaced(userId: string, patternId: string): Promise<void> {
  await updateDoc(paths.pattern(userId, patternId), {
    surfaced: true,
    surfacedAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
}

// ─── Milestones ──────────────────────────────────────────────────────────────

export async function createMilestone(
  userId: string,
  data: Omit<Milestone, 'id' | 'userId'>
): Promise<string> {
  const ref = await addDoc(paths.milestones(userId), { ...data, userId });
  return ref.id;
}

export async function getMilestones(userId: string): Promise<Milestone[]> {
  const q = query(paths.milestones(userId), orderBy('occurredAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as Milestone));
}

// ─── Memory Layer V2 (facts/episodes/playbook/commitments/suppression) ──────

const memoryPaths = {
  facts: (userId: string) => collection(getFirebaseDb(), 'users', userId, 'memory', 'facts', 'items'),
  episodes: (userId: string) => collection(getFirebaseDb(), 'users', userId, 'memory', 'episodes', 'items'),
  playbook: (userId: string) => collection(getFirebaseDb(), 'users', userId, 'memory', 'playbook', 'items'),
  commitments: (userId: string) => collection(getFirebaseDb(), 'users', userId, 'memory', 'commitments', 'items'),
  suppressionRules: (userId: string) => collection(getFirebaseDb(), 'users', userId, 'memory', 'suppressionRules', 'items'),
  suppressionRule: (userId: string, ruleId: string) =>
    doc(getFirebaseDb(), 'users', userId, 'memory', 'suppressionRules', 'items', ruleId),
};

function normalizeConcept(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function suppressionRuleId(itemKind: MemoryItemKind, conceptText: string): string {
  const normalized = normalizeConcept(conceptText).replace(/\s+/g, '-').slice(0, 80);
  return `${itemKind}:${normalized || 'concept'}`;
}

export async function createSuppressionRule(
  userId: string,
  conceptText: string,
  itemKind: MemoryItemKind
): Promise<void> {
  const now = Timestamp.now();
  const ruleId = suppressionRuleId(itemKind, conceptText);
  await setDoc(memoryPaths.suppressionRule(userId, ruleId), {
    userId,
    conceptText: conceptText.trim(),
    itemKind,
    status: 'active',
    createdAt: now,
    updatedAt: now,
  });
}

export async function setSuppressionRuleStatus(
  userId: string,
  ruleId: string,
  status: SuppressionRule['status']
): Promise<void> {
  await updateDoc(memoryPaths.suppressionRule(userId, ruleId), {
    status,
    updatedAt: Timestamp.now(),
  });
}

export async function deleteMemoryFact(userId: string, factId: string): Promise<void> {
  await deleteDoc(doc(getFirebaseDb(), 'users', userId, 'memory', 'facts', 'items', factId));
}

export async function deleteMemoryEpisode(userId: string, episodeId: string): Promise<void> {
  await deleteDoc(doc(getFirebaseDb(), 'users', userId, 'memory', 'episodes', 'items', episodeId));
}

export async function deleteMemoryPlaybookEntry(userId: string, entryId: string): Promise<void> {
  await deleteDoc(doc(getFirebaseDb(), 'users', userId, 'memory', 'playbook', 'items', entryId));
}

export async function deleteMemoryCommitment(userId: string, commitmentId: string): Promise<void> {
  await deleteDoc(doc(getFirebaseDb(), 'users', userId, 'memory', 'commitments', 'items', commitmentId));
}

export async function getAllMemoryFacts(userId: string): Promise<MemoryFact[]> {
  const snap = await getDocs(query(memoryPaths.facts(userId), orderBy('updatedAt', 'desc')));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as MemoryFact));
}

export async function getAllMemoryEpisodes(userId: string): Promise<MemoryEpisode[]> {
  const snap = await getDocs(query(memoryPaths.episodes(userId), orderBy('updatedAt', 'desc')));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as MemoryEpisode));
}

export async function getAllMemoryPlaybookEntries(userId: string): Promise<MemoryPlaybookEntry[]> {
  const snap = await getDocs(query(memoryPaths.playbook(userId), orderBy('updatedAt', 'desc')));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as MemoryPlaybookEntry));
}

export async function getAllMemoryCommitments(userId: string): Promise<MemoryCommitment[]> {
  const snap = await getDocs(query(memoryPaths.commitments(userId), orderBy('updatedAt', 'desc')));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as MemoryCommitment));
}
